/**
 * Style Clipboard
 *
 * A singleton buffer that holds the *visual style* fields copied from a node's
 * `data`. Used by the "Copy style" / "Paste style" actions so a user can lift the
 * look of one shape and stamp it onto others without affecting their text/content.
 *
 * Only style-related keys are retained; positional/content fields (label, id,
 * position, etc.) are deliberately excluded.
 */

/** The visual style fields we copy between nodes. */
export interface NodeStyle {
  bgColor?: unknown
  borderColor?: unknown
  borderWidth?: unknown
  borderStyle?: unknown
  textColor?: unknown
  fontSize?: unknown
  fontWeight?: unknown
  fontFamily?: unknown
  textAlign?: unknown
  verticalAlign?: unknown
  lineHeight?: unknown
  letterSpacing?: unknown
  fillOpacity?: unknown
  shadow?: unknown
  shadowColor?: unknown
  shadowBlur?: unknown
  textOpacity?: unknown
}

/** The exact set of `data` keys treated as "style". Order is not significant. */
export const STYLE_FIELDS = [
  'bgColor',
  'borderColor',
  'borderWidth',
  'borderStyle',
  'textColor',
  'fontSize',
  'fontWeight',
  'fontFamily',
  'textAlign',
  'verticalAlign',
  'lineHeight',
  'letterSpacing',
  'fillOpacity',
  'shadow',
  'shadowColor',
  'shadowBlur',
  'textOpacity',
] as const

/**
 * Singleton style clipboard. Lives in memory for the lifetime of the app/tab.
 */
export class StyleClipboard {
  private static instance: StyleClipboard
  private style: NodeStyle | null = null

  private constructor() {}

  static getInstance(): StyleClipboard {
    if (!StyleClipboard.instance) {
      StyleClipboard.instance = new StyleClipboard()
    }
    return StyleClipboard.instance
  }

  /**
   * Copy the style fields out of a node's `data` into the buffer.
   * Only keys present on `nodeData` are stored, so a paste only overwrites
   * fields the source actually defined.
   */
  copyStyle(nodeData: Record<string, unknown> | null | undefined): void {
    if (!nodeData) {
      this.style = null
      return
    }

    const next: NodeStyle = {}
    for (const key of STYLE_FIELDS) {
      if (key in nodeData) {
        ;(next as Record<string, unknown>)[key] = nodeData[key]
      }
    }
    this.style = next

    console.log('🎨 Copied style:', next)
  }

  /**
   * Get a shallow copy of the buffered style (or null when nothing is buffered).
   */
  getStyle(): NodeStyle | null {
    return this.style ? { ...this.style } : null
  }

  /** Whether the buffer currently holds a style. */
  hasStyle(): boolean {
    return this.style !== null
  }

  /** Clear the buffer. */
  clear(): void {
    this.style = null
  }
}
